import React from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';

const baseStyles = {
  cursor: 'pointer',
  borderRadius: theme.radii.input,
  fontWeight: theme.fontWeights.medium,
  transition: theme.transitions.default,
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
};

const VARIANTS = {
  primary: {
    ...baseStyles,
    padding: theme.spacing.smallButtonPadding,
    fontSize: theme.sizes.smallFontSize,
    background: theme.colors.primary,
    color: theme.colors.white,
    border: 'none',
    '&:hover:not(:disabled)': {
      background: theme.colors.primaryHover,
    },
  },
  'primary-lg': {
    ...baseStyles,
    padding: theme.spacing.buttonPadding,
    ...theme.typography.button,
    background: theme.colors.primary,
    color: theme.colors.white,
    border: 'none',
    borderRadius: theme.radii.inner,
    minWidth: '250px',
    '&:hover:not(:disabled)': {
      background: theme.colors.primaryHover,
      transform: 'translateY(-1px)',
      boxShadow: theme.shadows.buttonHover,
    },
    '&:active:not(:disabled)': {
      transform: 'translateY(0)',
    },
    '&:disabled': {
      ...baseStyles['&:disabled'],
      background: theme.colors.disabled,
    },
  },
  'warning-outline': {
    ...baseStyles,
    padding: theme.spacing.smallButtonPadding,
    fontSize: theme.sizes.smallFontSize,
    background: theme.colors.bgCard,
    color: theme.colors.warningText,
    border: `1px solid ${theme.colors.warningText}`,
    '&:hover:not(:disabled)': {
      background: theme.colors.warningBg,
      borderColor: theme.colors.warningHover,
      color: theme.colors.warningHover,
    },
    '&:disabled': {
      ...baseStyles['&:disabled'],
      background: theme.colors.bgMuted,
      color: theme.colors.border,
      borderColor: theme.colors.border,
    },
  },
  'danger-outline': {
    ...baseStyles,
    padding: theme.spacing.removeBtnPadding,
    fontSize: theme.sizes.smallFontSize,
    background: theme.colors.bgCard,
    color: theme.colors.danger,
    border: `1px solid ${theme.colors.dangerBorder}`,
    '&:hover:not(:disabled)': {
      background: theme.colors.dangerBg,
    },
  },
};

const styledButtons = {};
Object.keys(VARIANTS).forEach((variant) => {
  styledButtons[variant] = styledComponent('button', VARIANTS[variant]);
});

function Button({ variant, onClick, disabled, children, dataTestId }) {
  const StyledButton = styledButtons[variant];
  return (
    <StyledButton
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={dataTestId}
    >
      {children}
    </StyledButton>
  );
}

Button.propTypes = {
  variant: PropTypes.oneOf(Object.keys(VARIANTS)).isRequired,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
  dataTestId: PropTypes.string,
};

Button.defaultProps = {
  onClick: undefined,
  disabled: false,
  dataTestId: undefined,
};

export default Button;
